import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Favorite, FavoriteContextAPI } from "./type";
import favoriteService from "./services";
import { FAVORITE } from "./Constain";
import { useAuth } from "../Auth";
const FavoriteContext = createContext<FavoriteContextAPI>(
  {} as FavoriteContextAPI
);
const initialMeta = { page: 1, perPage: 20, hasMore: false };
const Provider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const userId = user?._id;
  const account = useRef(userId);
  const listRequest = useRef<AbortController>();
  const countRequest = useRef<AbortController>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [meta, setMeta] = useState(initialMeta);
  const [totalFavorite, setTotalFavorite] = useState(0);
  useLayoutEffect(() => {
    account.current = userId;
    setFavorites([]);
    setMeta(initialMeta);
    setTotalFavorite(0);
    setError(undefined);
    setLoading(false);
    return () => {
      account.current = undefined;
      listRequest.current?.abort();
      countRequest.current?.abort();
    };
  }, [userId]);
  const getFavorites = useCallback(
    async (page = 1) => {
      if (!userId) return;
      listRequest.current?.abort();
      const controller = new AbortController();
      listRequest.current = controller;
      setLoading(true);
      setError(undefined);
      try {
        const res = await favoriteService.getFavorites(page, controller.signal);
        if (!controller.signal.aborted && account.current === userId) {
          setFavorites((previous) => {
            if (page === 1) return res.data;
            const seen = new Set(previous.map((item) => item._id));
            return [
              ...previous,
              ...res.data.filter((item: Favorite) => !seen.has(item._id)),
            ];
          });
          setMeta(res.meta);
        }
      } catch (err) {
        if (!controller.signal.aborted && account.current === userId)
          setError(String(err));
      } finally {
        if (!controller.signal.aborted && account.current === userId)
          setLoading(false);
      }
    },
    [userId]
  );
  const getTotalFavorite = useCallback(async () => {
    if (!userId) return;
    countRequest.current?.abort();
    const controller = new AbortController();
    countRequest.current = controller;
    try {
      const res = await favoriteService.getTotalFavorites(controller.signal);
      if (!controller.signal.aborted && account.current === userId)
        setTotalFavorite(res.totalDocuments);
    } catch (err) {
      if (!controller.signal.aborted && account.current === userId)
        setError(String(err));
    }
  }, [userId]);
  const addToFavorite = useCallback(
    async (item: Favorite) => {
      if (!userId) return;
      setLoading(true);
      setError(undefined);
      try {
        await favoriteService.addToFavorite(item);
        if (account.current === userId) await getTotalFavorite();
      } catch (err) {
        if (account.current === userId) setError(String(err));
      } finally {
        if (account.current === userId) setLoading(false);
      }
    },
    [userId, getTotalFavorite]
  );
  const removeFavorite = useCallback(
    async (id: string) => {
      if (!userId) return;
      setLoading(true);
      setError(undefined);
      try {
        await favoriteService.removeFavorite(id);
        if (account.current !== userId) return;
        setFavorites((previous) => previous.filter((item) => item._id !== id));
        setTotalFavorite((count) => Math.max(0, count - 1));
      } catch (err) {
        if (account.current === userId) setError(String(err));
      } finally {
        if (account.current === userId) setLoading(false);
      }
    },
    [userId]
  );
  const value = useMemo(
    () => ({
      loading,
      error,
      favorite: FAVORITE,
      favorites,
      totalFavorite,
      meta,
      addToFavorite,
      getFavorites,
      getTotalFavorite,
      removeFavorite,
    }),
    [
      loading,
      error,
      favorites,
      totalFavorite,
      meta,
      addToFavorite,
      getFavorites,
      getTotalFavorite,
      removeFavorite,
    ]
  );
  return (
    <FavoriteContext.Provider value={value}>
      {children}
    </FavoriteContext.Provider>
  );
};
export const useFavorite = () => useContext(FavoriteContext);
export default Provider;
